import {Fragment, useEffect, useMemo, useState} from 'react';
import {Grid, Typography, makeStyles} from '@material-ui/core';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import {AgeniumQuestionTranscript, getTranscript} from '@/APIs/Agenium/api';
import {LoadingSpinner} from '@/components/ui/loading-spinner';
import {AgenuimContextType} from '@/Core/agenium/ageniumContext';
import {useApplicationData} from '@/Core/hooks/useApplicationData';
import {buildAgeniumReviewQuestions} from './common/ageniumReviewQuestions';

const useStyles = makeStyles((theme) => ({
    mainContainer: {
        backgroundColor: theme.palette.secondary.contrastText,
        justifyContent: 'space-between',
        [theme.breakpoints.up('sm')]: {
            padding: '0 50px 65px',
        },
        [theme.breakpoints.up('md')]: {
            padding: '0 50px 80px',
        },
        [theme.breakpoints.up('lg')]: {
            padding: '0 50px 40px',
        },
    },
    contentGrid: {
        marginBottom: theme.spacing(6),
        paddingTop: '30px',
        [theme.breakpoints.up('md')]: {
            marginBottom: 0,
            paddingTop: 0,
        },
    },
    title: {
        fontFamily: 'LoraRegular',
        fontSize: 36,
        marginBottom: theme.spacing(1),
        color: '#3F51B5',
        [theme.breakpoints.up('md')]: {
            fontSize: 44,
        },
    },
    descriptionGrid: {
        alignContent: 'center',
        marginBottom: theme.spacing(4),
        marginTop: theme.spacing(2),
        [theme.breakpoints.up('sm')]: {
            marginBottom: theme.spacing(6),
            marginTop: theme.spacing(2),
        },
        [theme.breakpoints.up('md')]: {
            marginBottom: theme.spacing(6),
            marginTop: theme.spacing(4),
        },
    },
    descriptionText: {
        fontFamily: 'FredokaRegular',
        letterSpacing: 'normal',
        fontSize: 16,
        fontWeight: 300,
        color: '#3F51B5',
        paddingRight: theme.spacing(6),
    },
    policyInfoGrid: {
        width: 'fit-content',
        whiteSpace: 'nowrap',
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: theme.spacing(4),
        borderTop: 'none',
        borderBottom: 'none',
        borderLeft: '1px solid #3F51B5',
    },
    policyText: {
        fontFamily: 'FredokaRegular',
        letterSpacing: 'normal',
        fontSize: 18,
        fontWeight: 300,
        marginBottom: 0,
        color: '#3F51B5',
        paddingRight: theme.spacing(1),
    },
    questionsContainer: {
        fontFamily: 'FredokaRegular',
        fontSize: 13,
        color: '#616161',
        border: '1px solid #C5CDF8',
        margin: '-16px 0 26px 0',
        marginBottom: '-26px',
        height: '9em',
        overflowY: 'scroll',
        resize: 'vertical',
        padding: '2px 5px',
        '&::-webkit-scrollbar': {
            width: '8px',
        },
        '&::-webkit-scrollbar-track': {
            background: '#eee',
        },
        '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
        },
        [theme.breakpoints.up('sm')]: {
            fontSize: 16,
            marginBottom: '-16px',
            height: '12em',
            padding: '4px 10px',
        },
        [theme.breakpoints.up('md')]: {
            height: '16em',
            padding: '10px 30px',
        },
    },
    questionRow: {
        minHeight: '2.5em',
        alignItems: 'flex-start',
        '&:not(:last-child)': {
            borderBottom: '1px solid #C5CDF8',
        },
        [theme.breakpoints.up('sm')]: {
            minHeight: '3em',
        },
        [theme.breakpoints.up('md')]: {
            minHeight: '3.5em',
        },
    },
    questionCell: {
        color: '#3F51B5',
        padding: '8px 4px',
        paddingRight: '8px',
        display: 'flex',
        alignItems: 'flex-start',
        wordBreak: 'break-word',
        hyphens: 'auto',
        WebkitHyphens: 'auto',
        MozHyphens: 'auto',
        hyphenateCharacter: '-',
        '& *': {
            hyphens: 'auto',
            WebkitHyphens: 'auto',
            MozHyphens: 'auto',
        },
        [theme.breakpoints.up('sm')]: {
            padding: '12px 16px',
            paddingRight: '12px',
        },
        [theme.breakpoints.up('md')]: {
            padding: '16px 20px',
            paddingRight: '16px',
        },
    },
    answerCell: {
        color: '#333',
        padding: '8px 4px',
        paddingLeft: '8px',
        wordBreak: 'break-word',
        hyphens: 'auto',
        WebkitHyphens: 'auto',
        MozHyphens: 'auto',
        hyphenateCharacter: '-',
        '& *': {
            hyphens: 'auto',
            WebkitHyphens: 'auto',
            MozHyphens: 'auto',
        },
        [theme.breakpoints.up('sm')]: {
            padding: '12px 16px',
            paddingLeft: '12px',
        },
        [theme.breakpoints.up('md')]: {
            padding: '16px 20px',
            paddingLeft: '16px',
        },
    },
    bottomGrid: {
        marginTop: theme.spacing(5),
        maxWidth: 860,
        marginBottom: 0,
        padding: 0,
        [theme.breakpoints.up('md')]: {
            marginTop: theme.spacing(5),
        },
        [theme.breakpoints.up('sm')]: {
            padding: '0 24px',
        },
    },
    bottomTextTitle: {
        fontFamily: 'LoraRegular',
        fontSize: 22,
        color: '#434343',
        marginBottom: theme.spacing(2),
    },
    bottomText: {
        fontSize: 14,
        fontWeight: 300,
        color: '#bcbcbc',
        '& ol': {
            paddingLeft: 20,
        },
        '& li': {
            fontFamily: 'FredokaRegular',
            fontSize: 16,
            color: '#979797',
            listStyleType: 'disc',
            display: 'list-item',
            '&:not(:last-child)': {
                paddingBottom: theme.spacing(2),
            },
        },
    },
}));

const UnderwritingReviewScreen = ({ageniumContext}: {ageniumContext: AgenuimContextType}) => {
    const classes = useStyles();
    const {applicationData} = useApplicationData();
    const [transcript, setTranscript] = useState<AgeniumQuestionTranscript[]>([]);
    const [error, setError] = useState<string>('');
    const [quote, setQuote] = useState({
        coverageAmount: 0,
        premium: 0,
        policy: 'Indexed Universal Life',
    });
    const [city, setCity] = useState<string>('');
    const [state, setState] = useState<string>('');
    const LoadingState = () => (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 pt-28 pb-4">
            <LoadingSpinner className="w-10 h-10" />
            <h2 className="text-muted-foreground">Loading...</h2>
        </div>
    );

    const filteredQuestions = useMemo(() => buildAgeniumReviewQuestions(transcript), [transcript]);

    useEffect(() => {
        if (!ageniumContext.applicationId) {
            setError('Agenium Case ID not found');
            return;
        }

        getTranscript(ageniumContext.applicationId)
            .then((data: AgeniumQuestionTranscript[]) => {
                setTranscript(data);
                setError('');
            })
            .catch(() => {
                setTranscript([]);
                setError('Failed to load transcript');
            });
    }, [ageniumContext.applicationId]);

    useEffect(() => {
        if (applicationData) {
            setCity(applicationData.ageniumFields?.find((v: any) => v.key === 'Case_Applicant_City')?.value ?? '');
            setState(applicationData.ageniumFields?.find((v: any) => v.key === 'Case_Applicant_State')?.value ?? '');
            setQuote((prevQuote) => ({
                ...prevQuote,
                coverageAmount:
                    applicationData.prosperQuoteResponse?.find((f: any) => f.fieldName === 'faceAmount')?.value ?? 0,
                premium:
                    applicationData.prosperQuoteResponse?.find((f: any) => f.fieldName === 'monthlyPremium')?.value ??
                    0,
            }));
        }
    }, [applicationData]);

    const numberWithCommas = (x: number, roundK = false) =>
        `${Math.round(roundK ? x / 1000 : x)
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${roundK ? 'k' : ''}`;
    useEffect(() => {
        ageniumContext.setCurrentValue('Yes');
    }, []);

    const purifyHtml = (html: string) => {
        return DOMPurify.sanitize(html, {
            USE_PROFILES: {html: true},
        });
    };

    return transcript.length > 0 ? (
        <Grid container className={classes.mainContainer} style={{maxWidth: '100%'}}>
            <Grid item xs={12} className={classes.contentGrid}>
                {' '}
                <>
                    <Typography variant="h3" className={classes.title}>
                        Review and Send for Decision
                    </Typography>
                    <Grid container direction={'row'} className={classes.descriptionGrid}>
                        <Grid item xs={7} md={7} direction={'column'}>
                            <Typography variant="subtitle2" className={classes.descriptionText}>
                                You're one step closer to protecting the ones you love. Based on the most common and
                                desired options, our experts have provided a few default policy design choices as a
                                guide. Please review your full application below and confirm that you agree with each
                                answer.
                            </Typography>
                        </Grid>

                        <Grid item xs={5} md={5} direction="column" className={classes.policyInfoGrid}>
                            <Typography variant="subtitle2" className={classes.policyText}>
                                <span>Policy: {quote.policy}</span>
                            </Typography>
                            <Typography variant="subtitle2" className={classes.policyText}>
                                <span>Coverage: ${numberWithCommas(quote.coverageAmount)}</span>
                            </Typography>
                            <Typography variant="subtitle2" className={classes.policyText}>
                                <span>Est. Premium: ${numberWithCommas(quote.premium)} per month</span>
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid id="reviewSummaryQuestions" container className={classes.questionsContainer}>
                        {filteredQuestions?.map((item, ix) => (
                            <Fragment key={item.id + ix}>
                                <Grid
                                    item
                                    container
                                    xs={12}
                                    className={classes.questionRow}
                                    style={{
                                        borderBottom:
                                            ix === filteredQuestions.length - 1 ? 'none' : '1px solid #C5CDF8',
                                    }}
                                >
                                    <Grid
                                        item
                                        xs={7}
                                        className={classes.questionCell}
                                        style={{color: '#3F51B5!important'}}
                                        lang="en"
                                    >
                                        {parse(purifyHtml(item?.question))}
                                    </Grid>
                                    <Grid item xs={5} className={classes.answerCell} lang="en">
                                        {parse(purifyHtml(item?.answer ?? ''))}
                                        {item?.answer2 && (
                                            <>
                                                <br />
                                                {parse(purifyHtml(item?.answer2))}
                                            </>
                                        )}
                                    </Grid>
                                </Grid>
                            </Fragment>
                        ))}
                    </Grid>
                    <Grid item xs={12} className={classes.bottomGrid}>
                        <Typography variant="h5" className={classes.bottomTextTitle}>
                            To proceed with your application, please confirm if any of the following statements are
                            true:
                        </Typography>
                        <Typography variant="subtitle2" className={classes.bottomText}>
                            <ol style={{paddingLeft: 20}}>
                                <li>
                                    No Proposed Insured, Owner, or Beneficiary has ever sold, transferred, or assigned a
                                    life insurance policy to a third party, including viatical settlement entities, life
                                    settlement entities, insurance companies, secondary market providers, or premium
                                    financing entities.
                                </li>
                                <li>
                                    No Proposed Insured, Owner, or Beneficiary has ever received any inducement, fee, or
                                    compensation related to the sale, transfer, assignment, or purchase of a life
                                    insurance policy.
                                </li>
                                <li>
                                    No Proposed Insured, Owner, or Beneficiary has ever entered into or planned to enter
                                    into any agreement or contract to sell, assign, or otherwise transfer ownership or
                                    beneficial interest in the policy being applied for.
                                </li>
                                <li>
                                    I am not considering replacing, changing, stopping premium payments, surrendering,
                                    or reducing benefits on any existing annuity or life insurance policy or contract.
                                </li>
                                <li>
                                    You affirm that you are the applicant, proposed insured, policy owner, and payor,
                                    and consent to completing and signing the life insurance application and receiving
                                    all related documents electronically.
                                </li>
                                <li>
                                    You further affirm that, to the best of your knowledge and belief, all information
                                    submitted in connection with this application is true, complete, and accurate.
                                </li>
                                <li>
                                    You are representing that you are a resident of {city}, {state} and that you will be
                                    signing this policy in {city}, {state}.
                                </li>
                            </ol>
                        </Typography>
                    </Grid>
                </>
            </Grid>
        </Grid>
    ) : error ? (
        <div>
            <p>Error: {error}</p>
        </div>
    ) : (
        <LoadingState />
    );
};

export default UnderwritingReviewScreen;
